// ═══════════════════════════════════════════════════════════════════════
// Temps — éléments communs des écrans de la DRH et de la RH (agent I) : accès à l'API
// /api/temps/rh, droits effectifs renvoyés par le serveur, menu « Temps », mises en forme.
// Le menu ne s'appuie sur AUCUNE liste de rôles : il affiche ce que le serveur autorise
// (/api/temps/rh/droits, /api/temps/referentiels/droits, /api/temps/pointage/droits).
// ═══════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, UsersRound, Inbox, CalendarDays, Lock, SlidersHorizontal, ShieldCheck, Gauge, History, ChevronLeft, ChevronRight } from 'lucide-react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Bouton, EtatVide, Squelette } from '../../design/composants';

const API = '/api/temps/rh';
export const lireRh = async (url, params) => (await axios.get(`${API}${url}`, { params })).data.data;
export const ecrireRh = async (methode, url, corps) => (await axios({ method: methode, url: `${API}${url}`, data: corps || {} })).data.data;
export const messageErreur = (e, defaut = 'Opération impossible. Réessayez.') => e?.response?.data?.message || defaut;

// ─── Droits effectifs (une lecture par utilisateur et par session, rechargeable) ────────────
let droitsEnCours = null; let droitsDe = null;
const lireSansRefus = (url) => axios.get(url).then((r) => r.data.data).catch(() => null);

/**
 * Droits effectifs du menu « Temps » : RH (écrans I), responsable (écrans H), réglages (agent D),
 * bornes (agent C). `utilisateur` : identifiant de la personne connectée (le cache lui est propre).
 */
export function chargerDroitsTemps(utilisateur = null, forcer = false) {
  if (!droitsEnCours || forcer || droitsDe !== utilisateur) {
    droitsDe = utilisateur;
    droitsEnCours = Promise.all([
      lireSansRefus(`${API}/droits`),
      lireSansRefus('/api/temps/equipe/droits'),
      lireSansRefus('/api/temps/referentiels/droits'),
      lireSansRefus('/api/temps/pointage/droits'),
    ]).then(([rh, equipe, reglages, pointage]) => ({ rh, equipe, reglages, pointage }));
  }
  return droitsEnCours;
}

export function useDroitsTemps() {
  const { employe } = useAuth();
  const id = employe?.id || null;
  const [droits, setDroits] = useState(null);
  useEffect(() => {
    let actif = true;
    if (id) chargerDroitsTemps(id).then((d) => { if (actif) setDroits(d); });
    return () => { actif = false; };
  }, [id]);
  return droits;
}

// ─── Menu « Temps » ─────────────────────────────────────────────────────────────────────────
// Ordre fixé par la conception (§ 14.4, § 14.5) ; « Mon équipe » (agent H) en tête pour qui encadre.
// « À traiter » : UNE boîte — celle de la RH si la personne y a accès, sinon celle du responsable.
export const ENTREES_TEMPS = Object.freeze([
  { cle: 'equipe', vers: '/temps/equipe', libelle: 'Mon équipe', Icone: UsersRound, visible: (d) => !!d.equipe?.mon_equipe },
  { cle: 'tableau_de_bord', vers: '/temps/rh', libelle: 'Tableau de bord', Icone: LayoutDashboard, visible: (d) => !!d.rh?.menu?.tableau_de_bord },
  { cle: 'a_traiter', vers: (d) => (d.rh?.menu?.a_traiter ? '/temps/rh/a-traiter' : '/temps/a-traiter'), libelle: 'À traiter', Icone: Inbox,
    visible: (d) => !!d.rh?.menu?.a_traiter || !!d.equipe?.a_traiter },
  { cle: 'journees', vers: '/temps/rh/journees', libelle: 'Journées', Icone: CalendarDays, visible: (d) => !!d.rh?.menu?.journees },
  { cle: 'cloture', vers: '/temps/rh/cloture', libelle: 'Clôture et paie', Icone: Lock, visible: (d) => !!d.rh?.menu?.cloture },
  { cle: 'reglages', vers: (d) => (d.reglages?.lecture ? '/temps/reglages/horaires' : '/temps/reglages/bornes'), libelle: 'Réglages', Icone: SlidersHorizontal,
    visible: (d) => !!d.reglages?.lecture || !!d.pointage?.gerer_bornes },
  { cle: 'revue', vers: '/temps/rh/revue', libelle: 'Revue', Icone: ShieldCheck, visible: (d) => !!d.rh?.menu?.revue },
  { cle: 'qualite', vers: '/temps/rh/qualite', libelle: 'Qualité', Icone: Gauge, visible: (d) => !!d.rh?.menu?.qualite },
  { cle: 'historique', vers: '/temps/historique', libelle: 'Historique (essais)', Icone: History, visible: (d) => !!d.rh?.menu?.historique },
]);

export const entreesVisibles = (d) => (d ? ENTREES_TEMPS.filter((e) => e.visible(d)).map((e) => ({ ...e, vers: typeof e.vers === 'function' ? e.vers(d) : e.vers })) : []);

/** Barre du menu « Temps » (onglets-liens), selon les droits effectifs. */
export function NavTemps({ actif }) {
  const droits = useDroitsTemps();
  const entrees = entreesVisibles(droits);
  if (!droits) return <div className="rh-onglets" aria-hidden="true" style={{ minHeight: 40 }} />;
  if (!entrees.length) return null;
  return (
    <nav className="rh-onglets" aria-label="Menu Temps">
      {entrees.map((l) => (
        <Link key={l.cle} to={l.vers} className="rh-onglet" aria-current={actif === l.cle ? 'page' : undefined}
          style={{ textDecoration: 'none', ...(actif === l.cle ? { borderBottomColor: 'var(--sg-primaire)', color: 'var(--sg-primaire)', fontWeight: 500 } : {}) }}>
          <l.Icone size={15} aria-hidden="true" />{l.libelle}
        </Link>
      ))}
    </nav>
  );
}

/** Entrée « /temps » : première rubrique autorisée par le serveur. */
export function TempsAccueil() {
  const droits = useDroitsTemps();
  if (!droits) return <Layout><div className="rh-page"><Squelette largeur="40%" /></div></Layout>;
  const entrees = entreesVisibles(droits);
  if (entrees.length) return <Navigate to={entrees[0].vers} replace />;
  return (
    <Layout>
      <div className="rh-page">
        <EtatVide titre="Aucun accès au suivi du temps"
          texte={droits.rh?.refus?.message || 'Le suivi du temps est réservé à la RH et aux personnes habilitées. Votre temps personnel est dans « Mon temps » de l\'application SOGAS Employé.'} />
      </div>
    </Layout>
  );
}

// ─── Mises en forme ─────────────────────────────────────────────────────────────────────────
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
/** « 2026-06 » → « juin 2026 ». */
export const moisLong = (m) => (m ? `${MOIS[Number(String(m).slice(5, 7)) - 1]} ${String(m).slice(0, 4)}` : '');
/** Minutes → « 7 h 05 » (0 → « 0 h »). */
export const heures = (min) => {
  if (min === null || min === undefined) return 'Non calculé';
  const a = Math.abs(Math.round(min)); const s = min < 0 ? '−' : '';
  return a % 60 ? `${s}${Math.floor(a / 60)} h ${String(a % 60).padStart(2, '0')}` : `${s}${Math.floor(a / 60)} h`;
};
export const pourcent = (v) => (v === null || v === undefined ? 'Non calculé' : `${String(v).replace('.', ',')} %`);
const FMT_DATE_HEURE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Africa/Dakar', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
export const dateHeure = (d) => (d ? FMT_DATE_HEURE.format(new Date(d)) : '');
const FMT_JOUR = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
/** « 2026-06-02 » → « mar. 2 juin ». */
export const jourCourt = (iso) => (iso ? FMT_JOUR.format(new Date(`${String(iso).slice(0, 10)}T12:00:00Z`)) : '');
export const nomPersonne = (p) => [p?.prenom, p?.nom].filter(Boolean).join(' ') || 'Non renseigné';
const FMT_AUJ = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Dakar' });
export const aujourdhui = () => FMT_AUJ.format(new Date());
export const ajouterJoursIso = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export const moisPrecedentIso = () => ajouterJoursIso(`${aujourdhui().slice(0, 7)}-01`, -1).slice(0, 7);

/** Libellé et ton d'un statut de période. */
export const STATUTS_PERIODE = Object.freeze({
  OUVERTE: ['neutre', 'Ouverte'], CLOTUREE: ['succes', 'Clôturée'], ROUVERTE: ['alerte', 'Rouverte'],
});
export const STATUTS_EXPORT = Object.freeze({
  GENERE: ['info', 'Prêt pour la paie'], INTEGRE: ['succes', 'Intégré en paie'], REMPLACE: ['neutre', 'Remplacé'],
});

/** Pagination par clé : « Plus récentes » / « Suivantes ». */
export function PagesParCle({ total, rang, nbPage, aSuivante, onPrecedente, onSuivante }) {
  if (!total) return null;
  return (
    <nav className="rh-pagination" aria-label="Pagination">
      <span>{rang + 1}–{rang + nbPage} sur {total}</span>
      <div className="rh-pagination-pages">
        <Bouton petit variante="discret" Icone={ChevronLeft} onClick={onPrecedente} disabled={rang === 0}>Précédentes</Bouton>
        <Bouton petit variante="discret" onClick={onSuivante} disabled={!aSuivante}>Suivantes<ChevronRight size={14} aria-hidden="true" /></Bouton>
      </div>
    </nav>
  );
}

/** Message de chargement des droits ou de refus, commun aux pages. */
export function AccesTemps({ droits, rubrique, children }) {
  if (!droits) return <Squelette largeur="60%" />;
  if (!droits.rh?.menu?.[rubrique]) {
    return <EtatVide titre="Accès non autorisé" texte={droits.rh?.refus?.message || 'Cette rubrique du temps n\'est pas ouverte à votre profil.'} />;
  }
  return children;
}
